import {Injectable} from '@angular/core';
import {RequesterService} from "../../../shared/services/requester/requester.service";
import {DbResponse} from "../../../shared/interceptors/db-response";
import {BehaviorSubject, filter, first, map} from "rxjs";
import {zip} from "rxjs";
import {HostingDataMapper} from "./info/hosting-data-mapper";
import {UserSubscriptionIds} from "../../../user/user-subscription";
import {DbStoreState} from '../../../store';
import {Store} from '@ngrx/store';
import * as fromStore from '../../../store';
import {Config} from "../../../config/config";
import {Website} from "../website";
import {IManualMigrationParams, IManualMigrationWebsiteCredentialsParams} from "./models/manual-migration-params.model";
import mockData from "../../../../assets/json/mock-data.json";

// A freshly created site answers /tenweb-get-ip with 403 until it is actually serving
const WHITELIST_IP_RETRIES = 3;
const WHITELIST_IP_RETRY_DELAY_MS = 1000;

@Injectable()
export class HostingService {
  public userSubscriptionIds: any = UserSubscriptionIds;
  private ownerWorkspaceId: any;
  private currentWorkspaceId: any;
  private currentWorkspaceRoleId: any;

  constructor(
    private _requester: RequesterService,
    private _store: Store<DbStoreState>
  ) {
    this._store.select(fromStore.getWorkspaceIdEntities).subscribe(workspaceIdEntities => {
      this.ownerWorkspaceId = workspaceIdEntities.ownerWorkspaceId;
      this.currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || this.ownerWorkspaceId;
      this.currentWorkspaceRoleId = workspaceIdEntities.currentWorkspaceRoleId;
    });
  }


  public generateSftpPassword(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/sftp/password`)
      .pipe(map((response: DbResponse): DbResponse => {
        if (response && response.success) {

        }
        return response;
      })));
  }

  public setupPasswordProtection(websiteId: number, action: string): BehaviorSubject<DbResponse> {
    const data = {
      "action": action
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/password-protection`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }


  public generateMysqlPassword(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/regenerate-db-password`)
      .pipe(map((response: DbResponse): DbResponse => {
        if (response && response.success) {

        }
        return response;
      })));
  }

  public checkSubDomain(formData: any): BehaviorSubject<DbResponse> {
    const data = {
      "subdomain": formData ? formData.toLowerCase() : ""
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/subdomain/check`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }


  public installMultisite(formData: any): BehaviorSubject<DbResponse> {
    const data = {
      "multisite_type": formData ? formData : ""
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/website`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }


  public checkDomain(formData: any): BehaviorSubject<DbResponse> {
    const data = {
      "domain_name": formData ? formData : ""
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/name/check`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public hostingToolsSettings(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['settings-hosting'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/settings`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        if (response && response.success) {
        }
        return response;
      })));
  }


  public hostingPhpConfigs(websiteId: number): BehaviorSubject<DbResponse> {
    const data = {
      "config_type": "all"
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/php-configs`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }


  public updatePhpConfigs(websiteId: number, [type, value]): BehaviorSubject<DbResponse> {
    const data = {
      "config_type": type,
      "config_value": value
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/php-configs/update`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public phpVersions(): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
        .get(`website/php-versions`, null, true)
        .pipe(map((response: DbResponse): DbResponse => {
          if (response && response.success) {
          }
          return response;
        })
    ));
  }

  public hostingToolsGetExcludePages(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['exclude'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/cache/exclude`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        if (response && response.success) {
        }
        return response;
      })));
  }


  public hostingToolsSetExcludePages(websiteId: number, formData: any): BehaviorSubject<DbResponse> {
    const data = {
      "pages": formData ? formData.toString().split(",").join("\n") : ""
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .put(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/cache/exclude`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }


  public clearAllCaches(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/all-caches/flush`, null, null)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public clearCache(websiteId: number, recache = true): BehaviorSubject<DbResponse> {
    let data = null;
    if (recache) {
      data = {
        recache: 1
      };
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .delete(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/cache`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public clearObCache(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/object-cache/flush`, null, null)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public enableCache(websiteId: number, cacheTime: number): BehaviorSubject<DbResponse> {
    const data: any = {
      cache_time: cacheTime
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .put(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/cache/enable`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public enableObCache(websiteId: number, action: string): BehaviorSubject<DbResponse> {
    const _data = {action};
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/object-cache/toggle`, null, _data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public changeCacheTTL(websiteId: number, cacheTime: number): BehaviorSubject<DbResponse> {
    const data: any = {
      cache_time: cacheTime
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .put(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/cache/change-ttl`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public disableCache(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .put(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/cache/disable`, null)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public changePhpVersion(websiteId: number, phpVersion: string): BehaviorSubject<DbResponse> {
    const data: any = {
      php_version: phpVersion
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/php-version/switch`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public restartPhp(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/php/restart`, null)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public regions(subscriptionId): BehaviorSubject<DbResponse> {
    let params: any = {};
    if (subscriptionId === this.userSubscriptionIds.TRIAL || subscriptionId === Config.defaultStarterPlanId) {
      params = Object.assign({}, params, {
        'ghost': 1,
        'subscription_id': subscriptionId
      });
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/regions/available`, params, false)
      .pipe(map((response: any) => {
        if (response && response.success) {
          return response;
        }
      })));
  }

  public getUserMainDomain(): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/main-domain`, null, false)
      .pipe(map((response: any) => {
        if (response && response.success) {
          return response;
        }
      })));
  }

  /**
   * Resolves once the IP is actually whitelisted. Callers that redirect the user into the site
   * afterwards must await this - see _postWhiteListIp for why a bare subscribe() is not enough.
   */
  public whiteListIp(websiteId: number, websiteUrl: string, attempt = 0): Promise<void> {
    return fetch(`${websiteUrl}/tenweb-get-ip`)
      .then(response => {
        // fetch only rejects on network/CORS errors - a freshly created site answers 403 until it
        // is serving, and that resolves. Posting the error body as the IP whitelists nothing.
        if (!response.ok) {
          throw new Error(`tenweb-get-ip responded ${response.status}`);
        }
        return response.text();
      })
      .then(ip => this._postWhiteListIp(websiteId, ip))
      .catch(error => {
        if (attempt < WHITELIST_IP_RETRIES) {
          return new Promise<void>(resolve => setTimeout(resolve, WHITELIST_IP_RETRY_DELAY_MS))
            .then(() => this.whiteListIp(websiteId, websiteUrl, attempt + 1));
        }
        console.warn('[hosting] tenweb-get-ip unavailable, whitelisting without IP', error);
        return this._postWhiteListIp(websiteId);
      });
  }

  /**
   * whiteListIpMainRequest returns a hot stream seeded with null (requester.service.ts:154-167),
   * so a bare subscribe() fires on that seed while the POST is still in flight. filter+first
   * waits for the real response.
   */
  private _postWhiteListIp(websiteId: number, ip: string = null): Promise<void> {
    return new Promise((resolve) => {
      this.whiteListIpMainRequest(websiteId, ip)
        .pipe(filter((response: DbResponse): boolean => !!response), first())
        .subscribe(() => resolve());
    });
  }

  public whiteListIpMainRequest(websiteId: number, ip = null): BehaviorSubject<DbResponse> {
    const data = ip ? { ip } : undefined; // If IP is not provided, omit the body
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/whitelist/ip`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public subdomainSuggestion(): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/subdomain/generate`)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public pushToLive(websiteId: number): BehaviorSubject<DbResponse> {
    const data: any = {
      type: ["db", "files"]
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/migrate-dev`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));

  }

  public syncWithLive(liveWebsiteId: number): BehaviorSubject<DbResponse> {
    const data: any = {
      type: ['db', 'files']
    };

    return this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${liveWebsiteId}/push-to-staging`, null, data);
  }

  public disableStagingEnvironment(devWebsiteId: number): BehaviorSubject<DbResponse> {
    return this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${devWebsiteId}/staging/disable`);
  }


  public getHostingInfoData(websiteId: number, demoUser = false): BehaviorSubject<DbResponse> {
    if (demoUser) {
      const response: DbResponse = new DbResponse();
      response.success = true;
      const _data = mockData['credentials'].data;
      response.data = HostingDataMapper.map(_data);
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(zip(
      this._requester.lxd
        .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/user_info`, null, true),
      this._requester.lxd
        .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/instance-info`, null, true),
    )
      .pipe(map((responseList: DbResponse[]) => {
        const hasNullResponse: boolean = !!responseList.filter((_response: DbResponse): boolean => !_response).length;

        if (hasNullResponse) {
          return null;
        }
        const failedResponse: DbResponse = responseList.find((_response: DbResponse): boolean => !_response.success);
        if (failedResponse) {
          return failedResponse;
        }

        const response: DbResponse = new DbResponse();
        response.success = true;

        response.data = HostingDataMapper.map(responseList);


        return response;
      })));
  }

  public createWebsite(formData: any, templateImport = false): BehaviorSubject<DbResponse> {
    let data =  {
      "subdomain": formData.subdomain.replace(/ /g, '').toLowerCase(),
      "region": formData.region,
      "create_zone": 1, // TODO formData.createZone ? 1 : 0,
      "site_title": formData.siteTitle,
      "admin_username": formData.admin_username,
      "admin_password": formData.adminPassword,
    };
    if (formData.domain_name && formData.domain_name !== "") {
      data["domain_name"] = formData.domain_name.replace(/ /g, '').toLowerCase();
    }
    if (formData.multisiteType) {
      data["multisite_type"] = formData.multisiteType;
    }
    if (formData.themeId) {
      data["theme_id"] = formData.themeId;
    }
    if (formData.colorPallet) {
      data["color_pallet"] = formData.colorPallet;
    }
    if (formData.fontFamily) {
      data["font_family"] = formData.fontFamily;
    }
    if (formData.pipeline) {
      data["pipeline"] = formData.pipeline;
    }
    if (templateImport) {
      data = Object.assign({}, data, {
        template_import: true
      });
    }

    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/website`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public createWebsiteForTrialUsers(formData: any, demoUser?: boolean): BehaviorSubject<DbResponse> {
    let data = {
      "site_title": formData.siteTitle
    };
    if (formData.region) {
      data["region"] = formData.region;
    }
    if (formData.themeId) {
      data["theme_id"] = formData.themeId;
    }
    if (formData.colorPallet) {
      data["color_pallet"] = formData.colorPallet;
    }
    if (formData.fontFamily) {
      data["font_family"] = formData.fontFamily;
    }
    if (formData.pipeline) {
      data["pipeline"] = formData.pipeline;
    }
    if (formData.wvc_import) {
      data["wvc_import"] = formData.wvc_import;
    }
    if (demoUser) {
      data = Object.assign({}, data, {
        template_import: true
      });
    }

    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/website/fetch`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public managedHosting(credentialType: 'cpanel' | string,
                        subDomain: string,
                        websiteCredentials: IManualMigrationWebsiteCredentialsParams,
                        websiteId?: number
  ): BehaviorSubject<DbResponse> {
    let data: IManualMigrationParams = {
      subdomain: subDomain,
      credential_type: credentialType,
      website_credentials: JSON.stringify(websiteCredentials),
      additional_info: websiteCredentials.additionalInfo,
    };

    if (websiteId) {
      data = Object.assign({}, data, {
        'domain_id': websiteId,
      });
    }

    return <BehaviorSubject<DbResponse>>(this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/manual-requests/migration/store`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public manual95Challenge(websiteId: any, websiteUrl): BehaviorSubject<DbResponse> {
    const data = {
      "subdomain": websiteUrl,
      "credential_type": "custom",
      "website_credentials": JSON.stringify({}),
      "type": "95challenge",
      "domain_id": websiteId
    };

    return <BehaviorSubject<DbResponse>>(this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/manual-requests/migration/store`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public manualOptimize(websiteId: any): BehaviorSubject<DbResponse> {
    const data = {
      "domain_id": websiteId
    };

    return <BehaviorSubject<DbResponse>>(this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/manual-requests/optimization/store`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public migrate(currentWebsiteData: any, twHostingData: any): BehaviorSubject<DbResponse> {
    const postData: any = {
      subdomain: twHostingData.subdomain.toLowerCase(),
      domain: currentWebsiteData.url,
      login: currentWebsiteData.username,
      password: currentWebsiteData.password,
      region: twHostingData.dataCenterLocation
    };

    if (twHostingData.domain_name && twHostingData.domain_name !== "") {
      postData["tp_domain_name"] = twHostingData.domain_name;
    }

    return this._requester.manager.post(`workspaces/${this.currentWorkspaceId}/migrate`, null, postData);
  }

  public autoMigration(twHostingData: any, domainId: number, checkScore: boolean = false, sendPassRecoveryEmail: boolean = false): BehaviorSubject<DbResponse> {
    let postData: any = {
      subdomain: twHostingData.subdomain.toLowerCase(),
      domain_id: domainId,
      region: twHostingData.region
    };

    if (checkScore) {
      postData = Object.assign({}, postData, {
        check_score: true,
      });
    }

    if (sendPassRecoveryEmail) {
      postData = Object.assign({}, postData, {
        send_password_recovery_email: true,
      });
    }

    if (twHostingData.domain_name && twHostingData.domain_name !== "") {
      postData["tp_domain_name"] = twHostingData.domain_name;
    }

    return this._requester.manager.post(`workspaces/${this.currentWorkspaceId}/connected-domain/migrate`, null, postData);
  }

  public manualMigration(twHostingData: any, filePath: string, extension: string): BehaviorSubject<DbResponse> {
    const postData: any = {
      subdomain: twHostingData.subdomain,
      region: twHostingData.region,
      download_route: filePath,
      live: 1,
      direct_download: 1,
      file_extension: extension,
      is_migration_assistant: 1
    };

    return this._requester.lxd.post(`workspaces/${this.currentWorkspaceId}/website/migrate`, null, postData);
  }

  public getDomains(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['domain-name'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/domain-name`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public getCertificates(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['certificate'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/certificate`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));

  }

  public getZones(): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/zone`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));

  }

  public makeDomainPrimary(websiteId, domainId): BehaviorSubject<DbResponse> {

    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/domain-name/${domainId}/default`, null, null)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public removeDomain(websiteId, domainId): BehaviorSubject<DbResponse> {

    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .delete(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/domain-name/${domainId}/delete`, null, null)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));

  }


  public removeZone(zoneId): BehaviorSubject<DbResponse> {

    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .delete(`workspaces/${this.currentWorkspaceId}/zone/${zoneId}`, null, null)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));

  }

  public checkPointedDomain(websiteId, domainNameIds: any, expanded: boolean = false): BehaviorSubject<DbResponse> {

    const data: object = {
      "domain_name_ids": domainNameIds,
      "expanded": expanded ? 1 : 0
    };

    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/troubleshoot`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public checkWebsiteAvailability(websiteId): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/connection/troubleshoot`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public checkWebsiteAvailabilityByUrl(websiteUrl): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.core
        .get(`workspaces/${this.currentWorkspaceId}/domains/availability`, {site_url: websiteUrl})
    );
  }

  public createDomain(websiteId, formData: any): BehaviorSubject<DbResponse> {
    const data = {
      "domain_name": formData.domain_name.replace(/ /g, '').toLowerCase(),
      "create_zone": 1, // TODO formData.createZone ? 1 : 0
    };

    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/domain-name`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public createZone(formData: any): BehaviorSubject<DbResponse> {
    const data = {
      "name": formData.domain_name.replace(/ /g, '').toLowerCase(),
    };

    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/zone/create`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }


  public getZoneById(zoneId): BehaviorSubject<DbResponse> {

    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/zone/${zoneId}/`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));

  }

  public addDnsRecord(zoneId, formData: any, zoneRecordId, domainName?: any, nsRecordValue?: any, website?: Website): BehaviorSubject<DbResponse> {
    let _formData = this.dnsRecordDataGenerate(zoneId, formData, zoneRecordId, domainName, nsRecordValue);
    if (website) {
      const data = {
        domain_id: website.id,
        site_url: website.site_url,
        parent_id: website.parentId
      };
      _formData = Object.assign({}, _formData, data);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/zone/${zoneId}/records`, null, _formData)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public updateDnsRecord(zoneId, formData: any, zoneRecordId, domainName?: any, nsRecordValue?: any, website?: Website): BehaviorSubject<DbResponse> {
    let _formData = this.dnsRecordDataGenerate(zoneId, formData, zoneRecordId, domainName, nsRecordValue);
    if (website) {
      const data = {
        domain_id: website.id,
        site_url: website.site_url,
        parent_id: website.parentId
      };
      _formData = Object.assign({}, _formData, data);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .patch(`workspaces/${this.currentWorkspaceId}/zone/${zoneId}/records/${zoneRecordId}/update`, null, _formData)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public removeZoneRecord(zoneId, zoneRecordId, website?: Website): BehaviorSubject<DbResponse> {
    let data = null;
    if (website) {
      data = {
        domain_id: website.id,
        site_url: website.site_url,
        parent_id: website.parentId
      };
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/zone/${zoneId}/records/${zoneRecordId}`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));

  }


  public dnsRecordDataGenerate(zoneId, formData: any, zoneRecordId, domainName?: any, nsRecordValue?: any): void {
    let data;
    const subdomain = formData.subdomain.replace('@', '');
    if (formData.type === 'PTR' || formData.type === 'CNAME') {
      data = {
        "type": formData.type,
        "name": subdomain,
        "value": formData.ptrvalue ? formData.ptrvalue : domainName,
        "ttl": formData.ttl
      };
    } else if (formData.type === 'AAAA' || formData.type === 'A') {
      data = {
        "type": formData.type,
        "name": subdomain,
        "value": nsRecordValue,
        "ttl": formData.ttl
      };
    } else if (formData.type === 'SPF') {
      data = {
        "type": formData.type,
        "name": subdomain,
        "value": formData.spfValue ? formData.spfValue : domainName,
        "ttl": formData.ttl
      };
    } else if (formData.type === 'MX' || formData.type === 'SRV') {
      data = {
        "type": formData.type,
        "name": subdomain,
        "value": nsRecordValue,
        "ttl": formData.ttl
      };
    } else {
      data = {
        "type": formData.type,
        "name": subdomain,
        "value": (formData.type === 'TXT' ? formData.txtValue : (formData.value ? formData.value : domainName)),
        "ttl": formData.ttl
      };
    }
    return data;
  }


  public removeRedirect(websiteId, domainNameId, ruleId): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .delete(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/domain-name/${domainNameId}/redirect-rules/${ruleId}/delete`, null, null)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));

  }

  public removeRedirectRules(websiteId, ruleId): BehaviorSubject<DbResponse> {
    const data: any = {
      rule_ids: ruleId
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .delete(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/redirect-rules/delete`, null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));

  }

  public getRedirects(websiteId): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['redirect-rules'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/redirect-rules`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public getLogs(websiteId, lines, logType): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['logs'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/logs?lines=${lines}&type=${logType}`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public getRestores(websiteId): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['backups'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/backups`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public getBackupSettings(websiteId): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['settings-backup'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/backup/settings`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public redirectItemDataGenerate(formData): any {
    const data = {
      "redirect_from": formData.redirectFrom.replace(/ /g, ''),
      "redirect_to": formData.redirectTo.replace(/ /g, ''),
      "status_code": formData.status,
    };
    return data;
  }


  public updateRedirect(websiteId, domainNameId, formData, ruleId): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .patch(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/redirect-rules/${ruleId}`, null, this.redirectItemDataGenerate(formData))
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public createRedirect(websiteId, domainNameId, formData): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/domain-name/${domainNameId}/redirect-rules`, null, this.redirectItemDataGenerate(formData))
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public startDownloadingCsv(websiteId) {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/redirect-rules/export`, null, false, true)
      .pipe(map((response: DbResponse) => {
        return response;
      })));
  }

  public uploadCsv(websiteId, file) {
    const data = {
      "csv_file": file
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/redirect-rules/import`, null, data, true)
      .pipe(map((response: DbResponse) => {
        return response;
      })));
  }

  public generateCustomCertificate(websiteId, dataValues: any): BehaviorSubject<DbResponse> {
    const data: any = {
      cert_file_content: dataValues.certFileContents,
      key_file_content: dataValues.keyFileContents,
      ca_file_content: dataValues.caFileContents,
      redirect_http: dataValues.redirectHttp
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/certificate/custom`, null, data, true)
      .pipe(map((response: DbResponse) => {
        return response;
      })));
  }

  public generateFreeCertificate(websiteId, dataValues: any, redirectHttp: any): BehaviorSubject<DbResponse> {
    const data: any = {
      domain_name_ids: dataValues,
      redirect_http: redirectHttp
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/certificate/free`, null, data, true)
      .pipe(map((response: DbResponse) => {
        return response;
      })));
  }

  public removeCertificate(websiteId, dataValues: any): BehaviorSubject<DbResponse> {
    const data: any = {
      domain_name_ids: dataValues
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .delete(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/certificate/remove`, null, data, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public getHostingResources(websiteId): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['storage-all'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/storage`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public getHostingVisits(websiteId): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['visitors-all'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/visitors`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public getHostingResourcesByWebsite(websiteId): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['storage'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/storage`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public getHostingVisitsByWebsite(websiteId): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/visitors-count`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public getHostingVisitsByWebsiteByDate(websiteId, date): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['visitors'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/visitors`, date, true)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public restoreHostingBackup(websiteId, name): BehaviorSubject<DbResponse> {
    const data = {
      name: name
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/backups/restore`, null, data, true)
      .pipe(map((response: DbResponse) => {
        return response;
      })));
  }

  public replaceSiteUrls(websiteId, _data): BehaviorSubject<DbResponse> {
    const data = {
      search_string: _data.searchString,
      replace_string: _data.replaceString
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/site-url/replace`, null, data, true)
      .pipe(map((response: DbResponse) => {
        return response;
      })));
  }

  public disablePasswordProtection(websiteId): BehaviorSubject<DbResponse> {
    const data = {
      action: 'disable'
    };
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/password-protection`, null, data, true)
      .pipe(map((response: DbResponse) => {
        return response;
      })));
  }


  public changeMainDomain(websiteId): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .patch(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/change-main-domain`, null, null)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public getAWSKeys(): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.lxd
      .get(`workspaces/${this.currentWorkspaceId}/website/migrate/get_s3_credentials`)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public setNameservers(domainNameId: number, nameserversData: any, isDefault: boolean): BehaviorSubject<DbResponse> {
    const url = `workspaces/${this.currentWorkspaceId}/domain-names/${domainNameId}/nameservers`;
    let body;

    if (isDefault) {
      body = {
        "default": true
      };
    } else {
      body = {
        "nameservers": nameserversData,
        "default": false
      };
    }

    return <BehaviorSubject<DbResponse>>(this._requester.core
        .put(url, null, body)
        .pipe(map((response: DbResponse): DbResponse => {
          return response;
        }))
    );
  }

}
